import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Panel flotante anclado a un control: donde va, y cuando se cierra.
 *
 * Esta separado porque lo usan SearchSelect y MultiSelect, y es la parte
 * con mas trampas de las dos: el portal, el volteo cuando no cabe abajo, el
 * seguimiento al scrollear y el clic afuera. Tenerlo dos veces significaria
 * arreglar cada bug dos veces.
 *
 * Devuelve:
 *   abierto, setAbierto
 *   triggerRef   va en el control que abre el panel
 *   panelRef     va en el panel
 *   pos          {left, right, width, top, bottom, maxAlto} o null si esta
 *                cerrado. Con anclaje "fin" viene right en vez de left, y
 *                sin width: el panel se mide solo.
 *
 * anclaje:
 *   "inicio"  (por defecto) el panel arranca donde el control y mide lo
 *             mismo. Es lo que quiere un desplegable de formulario.
 *   "fin"     el panel termina donde termina el control y mide lo que
 *             necesite. Es lo que quiere un menu colgado de un boton
 *             chico: con "inicio" saldria de 32 px de ancho.
 */

const MARGEN = 8;

// Se voltea hacia arriba solo si abajo no cabe algo razonable. Comparar
// contra el alto deseado lo hacia saltar hacia arriba con 300px libres,
// tapando los campos de arriba sin necesidad.
const MINIMO_ABAJO = 200;

export default function usePanelFlotante({
  altoLista = 260,
  extra = 62,
  anclaje = "inicio",
} = {}) {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState(null);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const recalcular = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const espacioAbajo = window.innerHeight - r.bottom - MARGEN;
    const espacioArriba = r.top - MARGEN;

    // La lista mas lo que la acompana (buscador, contador, acciones).
    const altoDeseado = altoLista + extra;

    const haciaArriba =
      espacioAbajo < MINIMO_ABAJO && espacioArriba > espacioAbajo;
    const disponible = haciaArriba ? espacioArriba : espacioAbajo;

    const alFinal = anclaje === "fin";

    setPos({
      left: alFinal ? null : Math.round(r.left),
      right: alFinal ? Math.round(window.innerWidth - r.right) : null,
      width: alFinal ? null : Math.round(r.width),
      top: haciaArriba ? null : Math.round(r.bottom + 6),
      bottom: haciaArriba ? Math.round(window.innerHeight - r.top + 6) : null,
      maxAlto: Math.max(140, Math.min(altoDeseado, disponible)),
    });
  }, [altoLista, extra, anclaje]);

  useLayoutEffect(() => {
    if (!abierto) return;
    recalcular();
  }, [abierto, recalcular]);

  // Si se scrollea o cambia el tamano de la ventana, el panel tiene que
  // seguir al control. true = captura tambien el scroll interno.
  useEffect(() => {
    if (!abierto) return;

    const alMover = () => recalcular();
    window.addEventListener("scroll", alMover, true);
    window.addEventListener("resize", alMover);

    return () => {
      window.removeEventListener("scroll", alMover, true);
      window.removeEventListener("resize", alMover);
    };
  }, [abierto, recalcular]);

  // Cerrar al clickear fuera. Hay que mirar el panel tambien: al estar en un
  // portal, no es descendiente del control.
  useEffect(() => {
    if (!abierto) return;

    const alClickear = (e) => {
      const dentroTrigger = triggerRef.current?.contains(e.target);
      const dentroPanel = panelRef.current?.contains(e.target);
      if (!dentroTrigger && !dentroPanel) setAbierto(false);
    };

    document.addEventListener("mousedown", alClickear);
    return () => document.removeEventListener("mousedown", alClickear);
  }, [abierto]);

  return { abierto, setAbierto, triggerRef, panelRef, pos };
}
